import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import {
  ArrivingFailed,
  ArrivingLayout,
  ArrivingPending,
} from "@/client/components/arriving-layout";
import { authClient } from "@/client/lib/auth-client";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { requireSignedIn } from "@/client/lib/session";
import { Consent } from "@/features/identity/components/consent";
import { consentClientQuery } from "@/features/identity/consent-query";

/** The rest of the query is the provider's signed request, sent back to it as it arrived. */
const consentSearch = z.object({
  client_id: z.string().min(1).max(2048),
  redirect_uri: z.string().max(2048).optional(),
  scope: z.string().max(2048).optional(),
});

export const Route = createFileRoute("/consent")({
  pendingMs: PENDING_MS,
  pendingComponent: ArrivingPending,
  errorComponent: ArrivingFailed,
  validateSearch: consentSearch,
  beforeLoad: ({ context, location }) => {
    // The loader asks the provider through the auth client, so its code downloads while the
    // session is read rather than after.
    void authClient().catch(() => undefined);
    return requireSignedIn(context.queryClient, location.href);
  },
  loaderDeps: ({ search }) => ({ clientId: search.client_id }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(consentClientQuery(deps.clientId)),
  head: async () => {
    const { identityCopy } = await import("@/features/identity/copy");
    return { meta: [{ title: pageTitle(identityCopy.consent.tab) }] };
  },
  component: ConsentPage,
});

function ConsentPage() {
  const { redirect_uri, scope } = Route.useSearch();
  // From the loader and the guard, not the cache: a company switch empties the cache, and the
  // screen keeps showing until `router.invalidate()` has read the new session.
  const client = Route.useLoaderData();
  const session = Route.useRouteContext();

  return (
    <ArrivingLayout>
      <Consent
        client={client}
        company={{
          id: session.companyId ?? "",
          name: session.companyName ?? "",
        }}
        email={session.email ?? ""}
        redirectUri={redirect_uri}
        writes={scope?.split(" ").includes("write") ?? false}
      />
    </ArrivingLayout>
  );
}
